/**
 * <hp-variant-logic>
 *
 * Adds dependent-option behaviour to the HeadsetPlus variant picker without
 * forking assets/variant-picker.js.
 *
 * Three jobs:
 *   1. Mark option values that are not reachable from the current selection,
 *      so the shopper can see which choices actually combine.
 *   2. Auto-repair. If a click would produce a combination that does not
 *      exist, silently re-point the stalest OTHER axis to the nearest valid
 *      value, then let the event through. The shopper never lands on a
 *      disabled Add to cart.
 *   3. Drive the optional grouping step (Computer / Desk phone) rendered by
 *      variant-main-picker.liquid.
 *
 * HOW IT HOOKS IN
 * ---------------
 * It listens for `change` on the <variant-picker> root in the CAPTURE phase.
 * VariantPicker registers `variantChanged` as a bubble listener on that same
 * element and the event target is a descendant, so capture always runs first.
 * That is a DOM spec guarantee, not a load-order accident.
 *
 * Because VariantPicker#buildRequestUrl derives its request from live DOM
 * state (`select option[selected], fieldset input:checked`) rather than from
 * the event, correcting the DOM during capture is enough. Propagation is not
 * stopped on the happy path, so exactly one fetch happens.
 *
 * WHAT SURVIVES A ROUND TRIP
 * --------------------------
 * assets/morph.js runs with `childrenOnly: true`, so attributes on the
 * <variant-picker> root are never touched while every descendant attribute is
 * replaced from the server response. Anything worth remembering lives on the
 * root as data-*; anything painted onto descendants is re-applied on
 * `variant:update`, which fires synchronously after the morph.
 *
 * Every lookup goes through `this.picker` rather than `this`, because the
 * morph may well replace this element's own node while the root survives.
 * The listeners live on the root, so the original instance keeps working.
 */

const ROOT_TAG = 'variant-picker';
const MATRIX_SELECTOR = 'script[data-hp-vp-matrix]';

/** Axis sentinel for "no value resolved". */
const NO_INDEX = -1;

class HpVariantLogic extends HTMLElement {
  /** @type {HTMLElement | null} */
  picker = null;

  /** @type {{v: number, o: number[][], x: number[][]} | null} */
  matrix = null;

  /** @type {Map<string, [number, number]>} optionValueId -> [axis, index] */
  idIndex = new Map();

  /** @type {number[]} axis indices, most recently touched first */
  touched = [];

  /** @type {boolean} */
  enabled = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  noteTimer = undefined;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  flashTimer = undefined;

  connectedCallback() {
    this.picker = this.closest(ROOT_TAG);
    if (!this.picker) return;

    // quick-add.js relocates the picker on mobile and morph can swap this
    // node. First instance to arrive owns the listeners; later ones stand
    // down, since the owner queries through the surviving root anyway.
    if (this.picker.hpVariantLogic) return;
    this.picker.hpVariantLogic = this;

    if (!this.readMatrix()) return;
    this.enabled = true;

    this.touched = (this.picker.dataset.hpTouched || '')
      .split(' ')
      .map((value) => Number.parseInt(value, 10))
      .filter((value) => Number.isInteger(value));

    this.picker.addEventListener('change', this.onChangeCapture, true);
    this.picker.addEventListener('variant:update', this.onAfterMorph);
    this.picker.addEventListener('click', this.onGroupClick);

    this.refresh();
  }

  /* ------------------------------------------------------------------ *
   * Matrix
   * ------------------------------------------------------------------ */

  readMatrix() {
    const node = this.picker?.querySelector(MATRIX_SELECTOR);
    if (!node?.textContent) return false;

    try {
      this.matrix = JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[hp-variant-logic] unreadable matrix, leaving stock behaviour in place', error);
      return false;
    }

    if (!Array.isArray(this.matrix?.o) || !Array.isArray(this.matrix?.x)) return false;

    this.idIndex = new Map();
    this.matrix.o.forEach((ids, axis) => {
      ids.forEach((id, index) => this.idIndex.set(String(id), [axis, index]));
    });

    // Drop rows Liquid could not resolve rather than guessing at them.
    const axes = this.matrix.o.length;
    this.matrix.x = this.matrix.x.filter((row) => {
      for (let axis = 0; axis < axes; axis += 1) if (row[axis] === NO_INDEX) return false;
      return true;
    });

    return this.matrix.x.length > 0;
  }

  get axisCount() {
    return this.matrix ? this.matrix.o.length : 0;
  }

  /** Variant id sits immediately after the axis columns; availability after that. */
  variantIdOf(row) {
    return row[this.axisCount];
  }

  availableOf(row) {
    return row[this.axisCount + 1] === 1;
  }

  /** Price in cents. Undefined on a matrix rendered before prices were added. */
  priceOf(row) {
    return row[this.axisCount + 2];
  }

  /* ------------------------------------------------------------------ *
   * Reading the current selection
   * ------------------------------------------------------------------ */

  /**
   * Current option index per axis.
   *
   * Radios are read by :checked, selects by live property rather than the
   * `selected` attribute, so the answer is right mid-interaction before
   * VariantPicker has updated anything.
   *
   * @param {Element} [overrideTarget] control whose value should win
   * @returns {number[]}
   */
  readCurrent(overrideTarget) {
    const current = new Array(this.axisCount).fill(NO_INDEX);
    if (!this.picker || !this.matrix) return current;

    for (const input of this.picker.querySelectorAll('fieldset input[type="radio"]:checked')) {
      const at = this.idIndex.get(input.dataset.optionValueId || '');
      if (at) current[at[0]] = at[1];
    }

    for (const select of this.picker.querySelectorAll('select')) {
      const option = select.options[select.selectedIndex];
      const at = option ? this.idIndex.get(option.dataset.optionValueId || '') : undefined;
      if (at) current[at[0]] = at[1];
    }

    if (overrideTarget) {
      const at = this.axisOf(overrideTarget);
      if (at) current[at[0]] = at[1];
    }

    // A single-value axis is skipped during render but still occupies a
    // column in every row. It can only ever be index 0.
    this.matrix.o.forEach((ids, axis) => {
      if (current[axis] === NO_INDEX && ids.length === 1) current[axis] = 0;
    });

    return current;
  }

  /** @returns {[number, number] | undefined} */
  axisOf(element) {
    if (!element) return undefined;
    let id = element.dataset?.optionValueId;
    if (!id && element instanceof HTMLSelectElement) {
      id = element.options[element.selectedIndex]?.dataset.optionValueId;
    }
    return id ? this.idIndex.get(String(id)) : undefined;
  }

  rowMatches(row, combo) {
    for (let axis = 0; axis < this.axisCount; axis += 1) {
      if (combo[axis] !== NO_INDEX && row[axis] !== combo[axis]) return false;
    }
    return true;
  }

  findRow(combo) {
    return this.matrix?.x.find((row) => this.rowMatches(row, combo));
  }

  /* ------------------------------------------------------------------ *
   * Repair
   * ------------------------------------------------------------------ */

  onChangeCapture = (event) => {
    if (!this.enabled) return;

    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;

    const at = this.axisOf(target);
    if (!at) return;
    const clickedAxis = at[0];

    this.remember(clickedAxis);

    const desired = this.readCurrent(target);
    if (this.findRow(desired)) {
      this.syncGroupFromSelection(desired);
      // Paint before the request, not after it. The radio's `checked` already
      // flipped; this is what makes that visible. Everything below this line
      // still happens at its own pace.
      this.paintSelection();
      return;
    }

    const winner = this.bestRepair(desired, clickedAxis);

    if (!winner) {
      // The clicked value belongs to no variant at all. Refuse rather than
      // firing a request that cannot resolve.
      event.stopImmediatePropagation();
      event.preventDefault();
      this.restore(clickedAxis);
      this.announce('That combination is not available.');
      return;
    }

    const moved = this.apply(winner, desired, clickedAxis);
    this.syncGroupFromSelection(winner);

    // After apply(), so an auto-repaired axis lights up in the same frame as
    // the one actually clicked rather than trailing it by a round trip.
    this.paintSelection();

    if (moved.length) {
      this.announce(
        `${moved.map((item) => `${item.option} changed to ${item.value}`).join(', ')} to match your selection.`
      );
    }
    // Propagation deliberately continues: VariantPicker now reads the
    // corrected DOM and issues exactly one request.
  };

  /**
   * Pick the row to move to. Lexicographic, lower is better:
   *   0    stay inside the active disclosure group
   *   1..n keep the most-recently-touched axes put, so the STALEST axis is
   *        the one that gets re-pointed
   *   n+1  smallest total index distance - value order is the merchant's own
   *        and is meaningful, e.g. USB-A sits next to USB-C
   *   n+2  prefer a purchasable variant
   *   n+3  row order, purely so the same click is always deterministic
   */
  bestRepair(desired, clickedAxis) {
    const candidates = this.matrix.x.filter((row) => row[clickedAxis] === desired[clickedAxis]);
    if (!candidates.length) return null;

    const activeGroup = this.picker?.dataset.hpGroup || '';
    const groupedAxis = this.groupedAxis();

    const order = [];
    for (const axis of this.touched) {
      if (axis !== clickedAxis && !order.includes(axis)) order.push(axis);
    }
    for (let axis = 0; axis < this.axisCount; axis += 1) {
      if (axis !== clickedAxis && !order.includes(axis)) order.push(axis);
    }

    let best = null;
    let bestScore = null;

    candidates.forEach((row, rowIndex) => {
      const score = [];

      score.push(
        activeGroup && groupedAxis !== NO_INDEX && this.groupAt(groupedAxis, row[groupedAxis]) !== activeGroup ? 1 : 0
      );

      for (const axis of order) score.push(row[axis] === desired[axis] ? 0 : 1);

      let distance = 0;
      for (const axis of order) {
        if (desired[axis] !== NO_INDEX) distance += Math.abs(row[axis] - desired[axis]);
      }

      score.push(distance);
      score.push(this.availableOf(row) ? 0 : 1);
      score.push(rowIndex);

      if (bestScore === null || HpVariantLogic.lower(score, bestScore)) {
        bestScore = score;
        best = row;
      }
    });

    return best;
  }

  static lower(a, b) {
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return a[i] < b[i];
    }
    return false;
  }

  /**
   * Write the winning row onto every axis except the one just clicked.
   * Never dispatches `change` - that would re-enter this handler and fire a
   * second request.
   */
  apply(row, desired, clickedAxis) {
    const moved = [];

    for (let axis = 0; axis < this.axisCount; axis += 1) {
      if (axis === clickedAxis || row[axis] === desired[axis]) continue;

      const id = this.matrix.o[axis]?.[row[axis]];
      if (id === undefined) continue;

      const control = this.picker?.querySelector(`[data-option-value-id="${id}"]`);
      if (!control) continue;

      if (control instanceof HTMLOptionElement) {
        // selectedOptionsValues reads the ATTRIBUTE, so move that, not just
        // the property.
        for (const option of control.closest('select')?.options || []) option.removeAttribute('selected');
        control.setAttribute('selected', 'selected');
        control.selected = true;
      } else if (control instanceof HTMLInputElement) {
        for (const sibling of control.closest('fieldset')?.querySelectorAll('input[type="radio"]') || []) {
          sibling.dataset.currentChecked = 'false';
          sibling.dataset.previousChecked = 'false';
        }
        control.checked = true;
        // morph.js preserves data-current-checked old-over-new, so a stale
        // value here would survive the round trip and mis-style the pill.
        control.dataset.currentChecked = 'true';
      }

      moved.push({ option: this.optionNameFor(control), value: this.valueLabelFor(control) });
      this.flash(control);
    }

    return moved;
  }

  restore(axis) {
    const combo = this.readCurrent();
    const id = this.matrix.o[axis]?.[combo[axis]];
    const control = id === undefined ? null : this.picker?.querySelector(`[data-option-value-id="${id}"]`);
    if (control instanceof HTMLInputElement) control.checked = true;
  }

  /* ------------------------------------------------------------------ *
   * Compatibility painting
   * ------------------------------------------------------------------ */

  /**
   * Mark values that cannot be reached without also moving another axis.
   * They stay clickable - repair handles them - but the shopper can see at a
   * glance which options actually combine with what they already picked.
   */
  /**
   * Paint the selected state from an explicit class, not from
   * `:has(.hp-vp__input:checked)`.
   *
   * After a morph the browser does not re-evaluate that :has() against the
   * new checked state, so the navy highlight stuck to the previously
   * selected row - and the drift accumulated on every axis the shopper
   * touched. The variant always resolved correctly; only the paint lagged.
   * Reflecting the `checked` attribute did not force the invalidation
   * either. A class toggle always invalidates, so it is authoritative once
   * JS is live; the CSS keeps the :has() rules for the pre-JS first paint
   * (see [data-hp-js] in variant-main-picker.liquid).
   *
   * SPLIT OUT OF refresh() ON PURPOSE. refresh() only runs after the fetch and
   * morph have completed, so while it owned this loop the selected fill did not
   * move until the network came back and a click felt dead. This is the cheap
   * half - class toggles and nothing else - so onChangeCapture can call it
   * synchronously on the click and let price deltas and compatibility catch up
   * with the morph.
   */
  paintSelection() {
    if (!this.picker) return;
    this.picker.dataset.hpJs = '1';
    for (const card of this.picker.querySelectorAll('.hp-vp__card')) {
      const input = card.querySelector('.hp-vp__input');
      card.classList.toggle('hp-vp__card--on', Boolean(input && input.checked));
    }
  }

  refresh() {
    if (!this.enabled || !this.picker || !this.matrix) return;

    this.paintSelection();

    const current = this.readCurrent();
    const currentRow = this.findRow(current);
    const basePrice = currentRow ? this.priceOf(currentRow) : undefined;

    for (const [id, position] of this.idIndex) {
      const control = this.picker.querySelector(`[data-option-value-id="${id}"]`);
      if (!control) continue;

      const [axis, index] = position;
      const probe = current.slice();
      probe[axis] = index;

      const exactRow = this.findRow(probe);
      const existsAtAll = this.matrix.x.some((row) => row[axis] === index);

      control.dataset.hpCompat = exactRow ? '1' : '0';

      // Only a value belonging to no variant at all is truly blocked.
      // "Needs another axis to move" must stay reachable.
      if (control instanceof HTMLInputElement) control.disabled = !existsAtAll;

      // What picking this would cost, relative to the current selection.
      // Landing row is the exact match where one exists, otherwise whatever
      // the repair would settle on, so the figure matches what actually
      // happens on click.
      const landingRow = exactRow || this.bestRepair(probe, axis);
      this.paintDelta(control, basePrice, landingRow ? this.priceOf(landingRow) : undefined);
    }

    this.syncGroupChips();
  }

  /**
   * Write a "+$58" style hint onto an option. Created in JS rather than
   * Liquid because the figure is relative to the current selection, and
   * because a morph would strip it either way.
   */
  paintDelta(control, basePrice, targetPrice) {
    const card = control.closest?.('.hp-vp__card');
    const text = card?.querySelector('.hp-vp__text');
    if (!card || !text) return;

    let node = card.querySelector('.hp-vp__delta');
    const usable =
      typeof basePrice === 'number' && typeof targetPrice === 'number' && basePrice !== targetPrice && !control.checked;

    if (!usable) {
      node?.remove();
      return;
    }

    const diff = targetPrice - basePrice;
    const label = `${diff > 0 ? '+' : '−'}${this.formatMoney(Math.abs(diff))}`;

    if (!node) {
      node = document.createElement('span');
      node.className = 'hp-vp__delta';
      text.after(node);
    }
    node.textContent = label;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    const rounded = Math.round(amount) === amount ? amount.toFixed(0) : amount.toFixed(2);
    return `$${rounded}`;
  }

  onAfterMorph = () => {
    // The morph replaced every descendant, including the matrix the server
    // just re-rendered. Re-read, then repaint.
    if (this.readMatrix()) this.refresh();
    // The note text and the highlight class were descendants too, so they
    // went with it. Both are parked on the root; put them back.
    this.restoreNote();
    this.restoreFlash();
    this.syncUrl();
  };

  /**
   * Keep ?variant= on the URL for every selection.
   *
   * VariantPicker only writes it when the clicked control carries
   * data-variant-id, which Liquid emits solely on the axis that resolves to a
   * single variant. Change any earlier axis and it takes the opposite branch
   * and DELETES the parameter, so most combinations were not addressable -
   * you could not link or bookmark them, and Google could not preselect them,
   * which its product-variant guidance requires.
   *
   * Runs after `variant:update`, so it lands after VariantPicker's own
   * history write rather than fighting it. Authoritative id comes from the
   * server-rendered JSON, not from the matrix.
   */
  syncUrl() {
    if (!this.picker || this.picker.dataset.templateProductMatch !== 'true') return;
    if (this.picker.closest('quick-add-dialog, dialog, product-card')) return;

    const node = this.picker.querySelector('script[type="application/json"]');
    if (!node?.textContent) return;

    let id;
    try {
      id = JSON.parse(node.textContent)?.id;
    } catch (error) {
      return;
    }
    if (!id) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(id)) return;
    url.searchParams.set('variant', String(id));
    history.replaceState({}, '', url.toString());
  }

  /* ------------------------------------------------------------------ *
   * Disclosure groups
   * ------------------------------------------------------------------ */

  groupedAxis() {
    const fieldset = this.picker?.querySelector('fieldset[data-hp-grouped]');
    const at = fieldset ? this.axisOf(fieldset.querySelector('input[data-option-value-id]')) : undefined;
    return at ? at[0] : NO_INDEX;
  }

  groupAt(axis, index) {
    const id = this.matrix?.o[axis]?.[index];
    if (id === undefined) return '';
    const control = this.picker?.querySelector(`[data-option-value-id="${id}"]`);
    return control?.closest('[data-hp-group]')?.dataset.hpGroup || '';
  }

  syncGroupFromSelection(combo) {
    const axis = this.groupedAxis();
    if (axis === NO_INDEX || !this.picker) return;
    const group = this.groupAt(axis, combo[axis]);
    if (group) this.picker.dataset.hpGroup = group;
    this.syncGroupChips();
  }

  syncGroupChips() {
    const active = this.picker?.dataset.hpGroup || '';
    for (const chip of this.picker?.querySelectorAll('[data-hp-group-choice]') || []) {
      chip.setAttribute('aria-checked', chip.dataset.hpGroupChoice === active ? 'true' : 'false');
    }
  }

  onGroupClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-hp-group-choice]') : null;
    if (!chip || !this.picker || !this.matrix) return;

    const group = chip.dataset.hpGroupChoice || '';
    this.picker.dataset.hpGroup = group;
    this.syncGroupChips();

    const axis = this.groupedAxis();
    if (axis === NO_INDEX) return;

    const current = this.readCurrent();
    if (this.groupAt(axis, current[axis]) === group) return;

    // The selection sits outside the group just opened. Move to the first
    // in-group value, preferring a purchasable one, and dispatch a real
    // change so the normal repair and fetch path runs.
    const ids = this.matrix.o[axis] || [];
    let fallback = null;

    for (let index = 0; index < ids.length; index += 1) {
      if (this.groupAt(axis, index) !== group) continue;

      const probe = current.slice();
      probe[axis] = index;
      const row = this.findRow(probe) || this.matrix.x.find((candidate) => candidate[axis] === index);
      if (!row) continue;

      if (this.availableOf(row)) {
        fallback = ids[index];
        break;
      }
      if (fallback === null) fallback = ids[index];
    }

    if (fallback === null) return;

    const control = this.picker.querySelector(`[data-option-value-id="${fallback}"]`);
    if (control instanceof HTMLInputElement) {
      control.checked = true;
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /* ------------------------------------------------------------------ *
   * Bookkeeping and announcements
   * ------------------------------------------------------------------ */

  remember(axis) {
    this.touched = [axis, ...this.touched.filter((value) => value !== axis)];
    if (this.picker) this.picker.dataset.hpTouched = this.touched.join(' ');
  }

  optionNameFor(control) {
    const fieldset = control.closest('[data-option-name]');
    return fieldset?.querySelector('.hp-vp__label')?.textContent?.trim() || 'Selection';
  }

  valueLabelFor(control) {
    if (control instanceof HTMLOptionElement) return control.textContent?.trim() || '';
    return (
      control.closest('.hp-vp__card')?.querySelector('.hp-vp__text')?.textContent?.trim() ||
      control.getAttribute('aria-label') ||
      control.value ||
      ''
    );
  }

  /**
   * Highlight a control the picker moved on the shopper's behalf.
   *
   * The class goes on a descendant, so the round-trip morph strips it. The
   * option-value id is parked on the root (which morph never touches) and
   * re-applied in onAfterMorph.
   */
  flash(control) {
    const id = control.dataset?.optionValueId;
    if (!this.picker || !id) return;

    const pending = new Set((this.picker.dataset.hpFlash || '').split(' ').filter(Boolean));
    pending.add(id);
    this.picker.dataset.hpFlash = [...pending].join(' ');

    this.paintFlash(id);

    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => {
      if (this.picker) delete this.picker.dataset.hpFlash;
      for (const card of this.picker?.querySelectorAll('.hp-vp__card--repaired') || []) {
        card.classList.remove('hp-vp__card--repaired');
      }
    }, 900);
  }

  paintFlash(id) {
    const card = this.picker?.querySelector(`[data-option-value-id="${id}"]`)?.closest('.hp-vp__card');
    if (!card) return;
    card.classList.remove('hp-vp__card--repaired');
    // Force a reflow so the highlight restarts on repeated repairs.
    void card.offsetWidth;
    card.classList.add('hp-vp__card--repaired');
  }

  restoreFlash() {
    for (const id of (this.picker?.dataset.hpFlash || '').split(' ').filter(Boolean)) this.paintFlash(id);
  }

  /**
   * Repairs are never silent. A shopper who believes they still have
   * "Standard QD" selected would receive the wrong cable.
   *
   * The live region is a descendant, so a morph blanks it - and the morph can
   * land in well under 100ms when the response is already cached. The message
   * is therefore parked on the root and re-applied afterwards.
   */
  announce(message) {
    if (!this.picker) return;
    this.picker.dataset.hpNote = message;
    this.restoreNote();

    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => {
      if (this.picker) delete this.picker.dataset.hpNote;
      const region = this.picker?.querySelector('[data-hp-vp-note]');
      if (region) region.textContent = '';
    }, 6000);
  }

  restoreNote() {
    // Re-query on every write. A morph can land between the call and the
    // animation frame, which would otherwise leave us writing into a node
    // that is no longer in the document.
    const write = () => {
      const region = this.picker?.querySelector('[data-hp-vp-note]');
      const message = this.picker?.dataset.hpNote || '';
      if (region && region.textContent !== message) region.textContent = message;
    };
    write();
    requestAnimationFrame(write);
  }
}

if (!customElements.get('hp-variant-logic')) {
  customElements.define('hp-variant-logic', HpVariantLogic);
}
